import convertRTEImages, { modifyNote } from 'utils/convertRTEImages'
import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'
import htmlToPlainText from 'utils/htmlToPlainText'
import type { CommitImage, CommitState, ImageUploadCommit } from './imageUploadWizard.types'

export type { CommitState }

/**
 * The entity-specific half of a commit. Each surface supplies one of these;
 * everything above it is shared.
 */
export interface ImageNoteAdapter {
	/** Writes the gallery row. `note` is PLAIN TEXT, not HTML. */
	createImageRow: (args: { image: CommitImage; note: string }) => Promise<void>
	/** Writes the note row. `html` is raw Quill HTML. */
	createNoteRow: (args: { html: string }) => Promise<void>
	/**
	 * Refetch and bump the parent's updateNote. Runs exactly once, last.
	 *
	 * This is mandatory, not an optimisation: the existing image and note
	 * slices write `entities` on create without appending to `ids`, so a new
	 * row stays invisible until something refetches.
	 */
	afterCommit?: () => Promise<void>
}

export interface CommitProgress {
	/** Fires after an image's rows are written, for per-row UI. */
	onImageCommitted?: (imageId: string) => void
	/** Fires after the shared note is written (plan === 'single'). */
	onNoteCommitted?: () => void
}

export const createCommitState = (): CommitState => ({
	committedImageIds: new Set<string>(),
	committedNoteImageIds: new Set<string>(),
	sharedNoteCommitted: false,
})

/**
 * Turn the editor's HTML into storable HTML.
 *
 * Images typed *into* the editor arrive as base64 embeds. They are uploaded
 * here and swapped for their Cloudinary URLs, then the staged images are
 * appended as plain <img> tags.
 *
 * Order matters. `modifyNote` walks the string replacing the nth `data:image`
 * src with uploads[n] positionally, so it must run before anything else is
 * appended. The appended tag shape is byte-identical to what NoteAddCN already
 * produces, which is what lets components/Note split it into its collage.
 */
export const buildNoteHtml = async (
	rawHtml: string,
	staged: CommitImage[],
	folder?: string,
): Promise<string> => {
	const embeddedFiles = convertRTEImages(rawHtml || '', '')

	let html = rawHtml || ''

	if (embeddedFiles.length > 0) {
		const embeddedUploads = []
		for (const file of embeddedFiles) {
			embeddedUploads.push(await uploadToCloudinary(file, folder ? { folder } : undefined))
		}
		html = modifyNote(html, embeddedUploads)
	}

	const appended = staged
		.map(image => `<p><img src="${image.upload.secure_url}"></p>`)
		.join('')

	return `${html}${appended}`
}

/**
 * Write the images and note(s) for one confirmed upload.
 *
 * Deliberately not transactional. There is no transaction boundary across
 * these endpoints, and soft-deleting a half-written note is worse than an
 * inconsistent-but-visible one. Instead the work is idempotent per item: on
 * failure the caller shows a retry that re-enters here with the same
 * `state`, and only the uncommitted steps run again.
 */
export const commitImageUpload = async (
	commit: ImageUploadCommit,
	adapter: ImageNoteAdapter,
	progress: CommitProgress = {},
	state: CommitState = createCommitState(),
	folder?: string,
): Promise<CommitState> => {
	const { plan, images, sharedNoteHtml } = commit

	try {
		if (plan === 'single' && !state.sharedNoteCommitted) {
			// One note holding every image. Written before the gallery rows,
			// matching the order the notes surfaces use.
			const html = await buildNoteHtml(sharedNoteHtml, images, folder)
			await adapter.createNoteRow({ html })
			state.sharedNoteCommitted = true
			progress.onNoteCommitted?.()
		}

		const sharedPlainNote = plan === 'single' ? htmlToPlainText(sharedNoteHtml) : ''

		for (const image of images) {
			if (state.committedImageIds.has(image.id)) continue

			if (plan === 'per-image' && !state.committedNoteImageIds.has(image.id)) {
				// One note per image, each holding exactly that image.
				const html = await buildNoteHtml(image.noteHtml, [image], folder)
				await adapter.createNoteRow({ html })
				state.committedNoteImageIds.add(image.id)
			}

			const note =
				plan === 'single'
					? sharedPlainNote
					: plan === 'per-image'
						? htmlToPlainText(image.noteHtml)
						: ''

			await adapter.createImageRow({ image, note })

			state.committedImageIds.add(image.id)
			progress.onImageCommitted?.(image.id)
		}
	} finally {
		// Always refetch, even after a failure, so the UI shows exactly what
		// actually landed rather than a stale view. Swallowed deliberately: a
		// failed refetch must not replace the real commit error on the way out.
		try {
			await adapter.afterCommit?.()
		} catch (refetchError) {
			console.error('[commitImageUpload] afterCommit failed', refetchError)
		}
	}

	return state
}

export default commitImageUpload
